import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";

// Interface para ofertas
export interface Offer {
  id: string;
  name: string;
  productId: string;
}

// Interface para produtos
export interface Product {
  id: string;
  label: string; // Mudando para corresponder ao formato esperado
  productId: string;
  options: any[]; // Ofertas como opções aninhadas
}

// Interface para opções selecionáveis (produtos ou ofertas)
export interface SelectOption {
  value: string; // ID da opção
  label: string; // Nome para exibição
  productId?: string; // ID do produto (para ofertas)
  isChildOption?: boolean; // Identifica se é uma oferta (opção filha)
}

interface GroupedSelectProps {
  products: Product[];
  placeholder?: string;
  onChange: (option: SelectOption | null) => void;
  className?: string;
  defaultValue?: SelectOption | null;
  value?: SelectOption | null;
  readOnly?: boolean;
  dropUp?: boolean;
  theme?: "light" | "dark";
  searchable?: boolean;
  searchPlaceholder?: string;
}

const GroupedSelect: React.FC<GroupedSelectProps> = ({
  products,
  placeholder = "Selecione um produto ou oferta",
  onChange,
  className = "",
  defaultValue = null,
  value,
  readOnly = false,
  dropUp = false,
  theme,
  searchable = true,
  searchPlaceholder = "Pesquisar produto ou oferta",
}) => {
  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(defaultValue);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const selectRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filtrar produtos e ofertas com base no termo de pesquisa.
  // - Se o produto bate, mostra ele + TODAS as ofertas (preserva contexto).
  // - Se só algumas ofertas batem, mostra o produto como cabeçalho + apenas as ofertas que casam.
  const filteredProducts = useMemo<Product[]>(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return products;

    return products.reduce<Product[]>((acc, product) => {
      const productMatches = product.label?.toLowerCase().includes(term);
      const matchingOffers = (product.options || []).filter((offer) =>
        offer.label?.toLowerCase().includes(term)
      );

      if (productMatches) {
        acc.push(product);
      } else if (matchingOffers.length > 0) {
        acc.push({ ...product, options: matchingOffers });
      }
      return acc;
    }, []);
  }, [products, searchTerm]);

  // Auto-focus no campo de busca ao abrir; limpar termo ao fechar.
  useEffect(() => {
    if (isOpen && searchable) {
      const id = setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
    if (!isOpen) {
      setSearchTerm("");
    }
  }, [isOpen, searchable]);

  useEffect(() => {
    if (value !== undefined) setSelectedOption(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && selectRef.current && !selectRef.current.contains(event.target as Node)) {
        requestAnimationFrame(() => {
          setIsOpen(false);
        });
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSelectClick = () => { 
    if (!readOnly) setIsOpen(!isOpen); 
  };
  
  const handleOptionClick = (option: SelectOption, event?: React.MouseEvent) => {
    if (readOnly) return;
    
    event?.stopPropagation();
    
    setSelectedOption(option);
    onChange(option);
    
    requestAnimationFrame(() => {
      setIsOpen(false);
    });
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedOption(null);
    onChange(null);
  };

  // ==============================
  // CLASSES DINÂMICAS
  // ==============================
  // Campo do select
  let selectClasses = "h-11 w-full rounded-lg border px-4 py-2.5 pr-11 text-sm shadow-theme-xs cursor-pointer flex items-center justify-between focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 ";
  // Label do valor selecionado
  let labelColor = "";
  // Chevron
  let chevronColor = "transition-transform duration-200 ";
  // Dropdown de opções
  let dropdownClasses = "absolute z-10 w-full rounded-lg shadow-lg mt-1 ";

  // LIGHT MODE
  if (theme === "light") {
    selectClasses += "border-gray-200 bg-white ";
    selectClasses += selectedOption ? "text-gray-800 " : "text-gray-400 ";
    selectClasses += readOnly ? "cursor-not-allowed opacity-75 " : "";
    labelColor = selectedOption ? "text-gray-800" : "text-gray-400";
    chevronColor += "text-gray-500";
    dropdownClasses += "bg-white border border-gray-300 ";
  }
  // DARK MODE
  else if (theme === "dark") {
    selectClasses += "border-gray-700 bg-gray-900 ";
    selectClasses += selectedOption ? "text-white/90 " : "text-gray-400 ";
    selectClasses += readOnly ? "cursor-not-allowed opacity-75 " : "";
    labelColor = selectedOption ? "text-white/90" : "text-gray-400";
    chevronColor += "text-gray-400";
    dropdownClasses += "bg-gray-900 border border-gray-700 ";
  }
  // PADRÃO (herda dark: do tema global)
  else {
    selectClasses += "border-gray-200 bg-transparent dark:border-gray-800 dark:bg-gray-900 ";
    selectClasses += selectedOption ? "text-gray-800 dark:text-white/90 " : "text-gray-400 dark:text-gray-400 ";
    selectClasses += readOnly ? "cursor-not-allowed opacity-75 " : "";
    labelColor = selectedOption ? "text-gray-800 dark:text-white/90" : "text-gray-400 dark:text-gray-400";
    chevronColor += "text-gray-500 dark:text-gray-400";
    dropdownClasses += "bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 ";
  }
  dropdownClasses += dropUp ? "bottom-full mb-1" : "top-full mt-1";

  return (
    <div className={`relative w-full ${className}`} ref={selectRef}>
      {/* Campo do select */}
      <div
        className={selectClasses}
        onClick={handleSelectClick}
      >
        <span className={labelColor}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <div className="flex items-center">
          {selectedOption && (
            <button
              onClick={handleClear}
              className="mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              ✕
            </button>
          )}
          <ChevronDown
            size={18}
            className={`${chevronColor} ${isOpen ? 'transform rotate-180' : ''}`}
          />
        </div>
      </div>

      {/* Dropdown com as opções */}
      {isOpen && (
        <div className={dropdownClasses}>
          {searchable && (
            <div className="p-2">
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  className="w-full rounded border border-gray-200 py-2 pl-10 pr-4 text-sm outline-none focus:border-primary dark:border-gray-800 dark:bg-gray-900 dark:text-white/90"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
            </div>
          )}

          <div className="max-h-60 overflow-y-auto">
            {filteredProducts.map((product) => (
              <div key={product.id} className="product-group">
                {/* Cabeçalho do grupo (produto) */}
                <div
                  className={`px-4 py-2 cursor-pointer font-medium ${theme === "dark"
                    ? "text-white/90 hover:bg-gray-800"
                    : theme === "light"
                      ? "text-gray-700 hover:bg-gray-100"
                      : "text-gray-700 dark:text-white/90 hover:bg-gray-100 dark:hover:bg-gray-800"
                    }`}
                  onClick={(e) => handleOptionClick(
                    {
                      value: product.id,
                      label: product.label,
                      productId: product.productId
                    },
                    e
                  )}
                >
                  {product.label}
                </div>

                {/* Ofertas do produto */}
                {product.options && product.options.length > 0 && (
                  <div>
                    {product.options.map((option) => (
                      <div
                        key={option.value}
                        className={`pl-8 pr-4 py-2 cursor-pointer text-sm ${theme === "dark"
                          ? "text-white/90 hover:bg-gray-800"
                          : theme === "light"
                            ? "text-gray-700 hover:bg-gray-100"
                            : "text-gray-700 dark:text-white/90 hover:bg-gray-100 dark:hover:bg-gray-800"
                          }`}
                        onClick={(e) => handleOptionClick(option, e)}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {filteredProducts.length === 0 && (
              <div className="px-4 py-2 text-gray-400 italic">
                {searchTerm ? "Nenhum resultado encontrado" : "Nenhum produto encontrado"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupedSelect;
